import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { PropertyService } from '@vsolv/packages/properties/web';
import { Asset } from '@wsphere/assets/domain';
import { Provisioning } from '@wsphere/warranties/domain';
import { BehaviorSubject, Observable, combineLatest, switchMap } from 'rxjs';
import { AssetService } from '../../services';

export interface AssetSubmissionValues {
  propertyValues: Record<string, unknown>;
  dimensionValues: Record<string, unknown>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-asset-details-card',
  templateUrl: './asset-details-card.component.html',
  standalone: false,
})
export class AssetDetailsCardComponent {
  private static propertySetCache = new Map<string, Promise<PropertySet.Model>>();
  private static submissionValuesCache = new Map<string, Promise<AssetSubmissionValues>>();

  constructor(
    private propertySvc: PropertyService,
    private assetSvc: AssetService
  ) {}
  @Output() edit = new EventEmitter<Asset.Model>();
  @Output() deleted = new EventEmitter<string>();
  @PropertyListener('asset') asset$ = new BehaviorSubject<Asset.Model | null>(null);
  @Input() asset!: Asset.Model;

  @PropertyListener('staffView') staffView$ = new BehaviorSubject<boolean>(false);
  @Input() staffView = false;

  @Input() selected = false;
  @Input() disabled = false;
  @Input() minimized = false;

  @PropertyListener('session') session$ = new BehaviorSubject<Provisioning.ProvisioningSession | null>(null);
  @Input() session?: Provisioning.ProvisioningSession | null;

  @Output() clicked = new EventEmitter();

  propertySet$: Observable<PropertySet.Model | null> = this.asset$.pipe(
    switchMap(async asset => {
      if (!asset) return null;
      if (asset.propertySet) return asset.propertySet;

      const cacheKey = asset.propertySetId;
      const cached = AssetDetailsCardComponent.propertySetCache.get(cacheKey);
      if (cached) return cached;

      const request = this.propertySvc.retrievePropertySet(asset.propertySetId).catch(err => {
        AssetDetailsCardComponent.propertySetCache.delete(cacheKey);
        throw err;
      });
      AssetDetailsCardComponent.propertySetCache.set(cacheKey, request);
      return request;
    })
  );

  submissionValues$: Observable<AssetSubmissionValues> = combineLatest([
    this.asset$,
    this.staffView$,
    this.session$,
  ]).pipe(
    switchMap(async ([asset, staffView, session]) => {
      if (!asset) return { propertyValues: {}, dimensionValues: {} };

      // The customer-assets list already returns the latest submission's values and dimension values,
      // so use them instead of one request per card. Only fetch when the asset came without them.
      if (asset.values) {
        return {
          propertyValues: asset.values,
          dimensionValues: asset.dimensionValues ?? {},
        };
      }

      const cacheKey = `${asset.id}|${staffView ? 'staff' : 'portal'}|${session?.id ?? ''}`;
      const cached = AssetDetailsCardComponent.submissionValuesCache.get(cacheKey);
      if (cached) return cached;

      const request = (async () => {
        const submission =
          staffView && session
            ? await this.assetSvc.getAssetValuesForSales(asset.id, session.id)
            : await this.assetSvc.getAssetValues(asset.id);
        return {
          propertyValues: (submission.value ?? asset.values ?? {}) as Record<string, unknown>,
          dimensionValues: (submission.dimensionValues ?? {}) as Record<string, unknown>,
        };
      })().catch(err => {
        AssetDetailsCardComponent.submissionValuesCache.delete(cacheKey);
        throw err;
      });

      AssetDetailsCardComponent.submissionValuesCache.set(cacheKey, request);

      return request;
    })
  );

  /** Clear cached submission values after an asset edit so the preview refreshes. */
  static clearSubmissionCache(assetId?: string) {
    if (!assetId) {
      AssetDetailsCardComponent.submissionValuesCache.clear();
      return;
    }
    for (const key of AssetDetailsCardComponent.submissionValuesCache.keys()) {
      if (key.startsWith(`${assetId}|`)) {
        AssetDetailsCardComponent.submissionValuesCache.delete(key);
      }
    }
  }

  selectAsset() {
    this.clicked.emit(this.asset);
  }

  async deleteAsset() {
    if (!this.asset) return;
    this.deleted.emit(this.asset.id);
  }

  editAsset() {
    this.edit.emit(this.asset);
    return false;
  }
}
